/* Product-scoped reviews. Customer text is always inserted as text, never HTML. */
if (!customElements.get('larbre-product-reviews')) {
  customElements.define('larbre-product-reviews', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.config = JSON.parse(this.querySelector('[data-config]').textContent);
      this.grid = this.querySelector('[data-grid]');
      this.status = this.querySelector('[data-status]');
      this.viewer = this.querySelector('[data-viewer]');
      this.limit = 20;
      this.rows = null;
      this.filtered = [];
      this.rating = null;
      this.sort = 'featured';
      this.dateFormat = new Intl.DateTimeFormat(this.config.strings.date_locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
      const average = this.querySelector('[data-average-stars]');
      average.replaceChildren(...this.stars(Math.round(Number(average.dataset.rating) * 2) / 2).childNodes);
      this.querySelector('[data-single-star]').replaceChildren(...this.stars(1, 1).childNodes);
      this.on(this, 'click', event => this.handleClick(event));
      this.on(this, 'keydown', event => this.handleKey(event));
      this.on(document, 'click', event => {
        if (!this.querySelector('.lpr__toolbar').contains(event.target)) this.closePanels();
      });
      for (const dialog of [this.viewer]) {
        this.on(dialog, 'click', event => {
          const rect = dialog.getBoundingClientRect();
          if (event.target === dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) dialog.close();
        });
        this.on(dialog, 'close', () => this.returnFocus?.focus({ preventScroll: true }));
      }
      this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
      this.resizeObserver.observe(this.grid);
      this.on(window, 'resize', () => this.scheduleLayout());
      document.fonts?.ready.then(() => this.isConnected && this.scheduleLayout());
      this.intersectionObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { this.intersectionObserver.disconnect(); this.load(); }
      }, { rootMargin: '700px' });
      this.intersectionObserver.observe(this);
    }

    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.resizeObserver?.disconnect(); this.intersectionObserver?.disconnect();
      cancelAnimationFrame(this.layoutFrame);
      this.viewer?.close();
    }

    on(target, event, callback, capture = false) { target.addEventListener(event, callback, { signal: this.controller.signal, capture }); }
    text(key, values = {}) { return Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`[${name}]`, String(value)), this.config.strings[key]); }
    element(tag, className, text) {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (text !== undefined) element.textContent = text;
      return element;
    }
    stars(rating, count = 5) {
      const stars = this.element('span', 'lpr__stars');
      stars.setAttribute('role', 'img'); stars.setAttribute('aria-label', this.text('rating_label', { rating }));
      const ns = 'http://www.w3.org/2000/svg';
      // Pointed stars match the review widget; no dependency on the theme's tile icons.
      const outer = 'm12 19.261-7.416 4.151 1.656-8.335L0 9.306l8.44-1.002L12 .588l3.56 7.717L24 9.306l-6.24 5.77 1.656 8.336L12 19.262Z';
      const outline = outer + 'm0-2.41 4.465 2.5-.997-5.019 3.756-3.475-5.081-.602L12 5.608l-2.143 4.648-5.081.601 3.756 3.475-.997 5.019L12 16.851Z';
      for (let i = 0; i < count; i++) {
        const svg = document.createElementNS(ns, 'svg');
        svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('aria-hidden', 'true');
        const path = document.createElementNS(ns, 'path'); path.setAttribute('fill', 'currentColor'); path.setAttribute('fill-rule', 'evenodd');
        path.setAttribute('d', rating >= i + 1 ? outer : outline); svg.append(path);
        if (rating > i && rating < i + 1) {
          const fill = document.createElementNS(ns, 'path'); fill.setAttribute('d', outer); fill.setAttribute('fill', 'currentColor'); fill.style.clipPath = 'inset(0 50% 0 0)'; svg.append(fill);
        }
        stars.append(svg);
      }
      return stars;
    }

    async load() {
      if (this.rows) return true;
      if (this.loading) return this.loading;
      this.status.textContent = this.text('loading'); this.status.classList.remove('lpr__status--quiet');
      this.querySelector('[data-retry]').hidden = true;
      this.loading = (async () => {
        try {
          const response = await fetch(this.config.url, { signal: this.controller.signal });
          if (!response.ok) throw new Error(`Review data: ${response.status}`);
          const data = await response.json();
          if (data.productId !== this.config.productId || !Array.isArray(data.reviews) || data.reviews.length !== data.count) throw new Error('Invalid review data');
          this.setDateOffset(data.dateAnchor);
          this.rows = data.reviews; this.buildHistogram(); this.filterAndRender(); return true;
        } catch (error) {
          this.rows = null;
          if (error.name !== 'AbortError') { this.status.textContent = this.text('error'); this.querySelector('[data-retry]').hidden = false; }
          return false;
        } finally { this.loading = null; }
      })();
      return this.loading;
    }

    buildHistogram() {
      const histogram = this.querySelector('[data-histogram]'); histogram.replaceChildren();
      for (let rating = 5; rating >= 1; rating--) {
        const count = this.rows.filter(row => row.rating === rating).length;
        const button = this.element('button', 'lpr__histogram-row'); button.type = 'button'; button.dataset.star = rating;
        button.setAttribute('aria-label', this.text('stars_filter', { rating, count })); button.setAttribute('aria-pressed', 'false'); button.disabled = !count;
        const bar = this.element('span', 'lpr__histogram-bar'), fill = this.element('span');
        fill.style.width = `${this.rows.length ? 100 * count / this.rows.length : 0}%`; bar.append(fill);
        button.append(this.stars(rating), bar, this.element('span', 'lpr__histogram-count', `(${count})`)); histogram.append(button);
      }
    }
    async applyFilters() { this.limit = 20; if (await this.load()) this.filterAndRender(); }
    filterAndRender() {
      this.filtered = this.rows.filter(row => !this.rating || row.rating === this.rating);
      if (this.sort === 'newest') this.filtered.sort((a, b) => Date.parse(b.date) - Date.parse(a.date));
      if (this.sort === 'highest') this.filtered.sort((a, b) => b.rating - a.rating);
      if (this.sort === 'lowest') this.filtered.sort((a, b) => a.rating - b.rating);
      this.querySelectorAll('[data-star]').forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.star) === this.rating)));
      this.querySelectorAll('[data-sort]').forEach(button => button.setAttribute('aria-checked', String(button.dataset.sort === this.sort)));
      this.resizeObserver.disconnect(); this.resizeObserver.observe(this.grid);
      this.grid.replaceChildren(); this.renderNext();
    }
    image(row, className, lazy = true) {
      const img = this.element('img', className);
      img.src = new URL(row.image, new URL(this.config.imageBase, document.baseURI)).href;
      img.alt = this.text('photo_alt', { name: row.name }); img.width = row.width; img.height = row.height;
      img.loading = lazy ? 'lazy' : 'eager'; img.decoding = 'async'; return img;
    }
    setDateOffset(anchor, now = Date.now()) {
      // One whole-day UTC offset keeps the corpus's age distribution and purchase gaps.
      // Fix it for this page load so cards, pagination and the lightbox always agree.
      const day = 86400000, baseline = Date.parse(anchor);
      this.dateOffset = Number.isFinite(baseline) ? Math.max(0, Math.floor(now / day) - Math.floor(baseline / day)) * day : 0;
    }
    date(value) {
      const shifted = new Date(Date.parse(value) + (this.dateOffset || 0));
      const date = this.element('time', 'lpr__date', this.dateFormat.format(shifted)); date.dateTime = shifted.toISOString(); return date;
    }
    reviewText(row) {
      const paragraph = this.element('p', 'lpr__text');
      const start = row.highlight ? row.text.indexOf(row.highlight) : -1;
      if (start < 0) paragraph.textContent = row.text;
      else paragraph.append(document.createTextNode(row.text.slice(0, start)), this.element('mark', '', row.highlight), document.createTextNode(row.text.slice(start + row.highlight.length)));
      if (!this.config.translatedLabel) return paragraph;
      // es locale: translated text by default; "Ver original" swaps this one review to the source file's text (loaded once, on first use).
      const showing = this.originalIds?.has(row.id) && this.originals?.has(row.id);
      if (showing) paragraph.textContent = this.originals.get(row.id);
      const wrap = this.element('div', 'lpr__review-text'); wrap.dataset.textFor = row.id;
      const foot = this.element('div', 'lpr__translation');
      if (!showing && !this.config.originalUrl) foot.append(this.element('span', 'lpr__translated', this.config.translatedLabel));   // with the link present, the link alone says it ("Ver original en inglés")
      if (this.config.originalUrl) {
        const toggle = this.element('button', 'lpr__original-toggle', showing ? this.text('show_translation') : this.text('show_original'));
        toggle.type = 'button'; toggle.dataset.originalToggle = row.id; toggle.setAttribute('aria-pressed', String(Boolean(showing))); foot.append(toggle);
      }
      wrap.append(paragraph, foot); return wrap;
    }
    async toggleOriginal(id) {
      this.originalIds ??= new Set();
      if (!this.originals) {
        try {
          const response = await fetch(this.config.originalUrl, { signal: this.controller.signal });
          const data = await response.json(); this.originals = new Map(data.reviews.map(review => [review.id, review.text]));
        } catch (error) { return; }
      }
      if (this.originalIds.has(id)) this.originalIds.delete(id); else this.originalIds.add(id);
      const row = this.rows?.find(review => review.id === id); if (!row) return;
      this.querySelectorAll(`[data-text-for="${CSS.escape(id)}"]`).forEach(node => node.replaceWith(this.reviewText(row)));
      this.scheduleLayout();
      this.querySelector(`[data-original-toggle="${CSS.escape(id)}"]`)?.focus({ preventScroll: true });
    }
    verifiedBadge(withLabel = false) {
      const badge = this.element('span', 'lpr__verified');
      badge.setAttribute('title', this.text('verified_source'));
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('aria-hidden', 'true');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('fill', 'currentColor');
      path.setAttribute('d', 'm10.6 16.6 7.05-7.05-1.4-1.4-5.65 5.65-2.85-2.85-1.4 1.4 4.25 4.25ZM12 22a10 10 0 1 1 0-20 10 10 0 0 1 0 20Z');
      path.setAttribute('fill-rule', 'evenodd'); svg.append(path); badge.append(svg);
      if (withLabel) badge.append(this.element('span', '', this.text('verified')));
      else { badge.setAttribute('role', 'img'); badge.setAttribute('aria-label', this.text('verified_source')); }
      return badge;
    }
    reviewBody(row) {
      const body = this.element('div', 'lpr__card-body');
      const title = this.element('div', 'lpr__card-title'); title.append(this.element('strong', 'lpr__name', row.name));
      if (row.sourceVerified === true) title.append(this.verifiedBadge());
      body.append(title, this.date(row.date), this.stars(row.rating), this.reviewText(row)); return body;
    }
    renderNext() {
      const previousCount = this.grid.children.length, fragment = document.createDocumentFragment();
      this.filtered.slice(previousCount, this.limit).forEach((row, offset) => {
        const card = this.element('article', 'lpr__card'); card.dataset.reviewId = row.id; card.dataset.openReview = previousCount + offset;
        card.tabIndex = 0; card.setAttribute('role', 'button'); card.setAttribute('aria-haspopup', 'dialog'); card.setAttribute('aria-label', this.text('open_review', { name: row.name }));
        if (row.image) {
          const media = this.element('div', 'lpr__card-media'); media.append(this.image(row, 'lpr__photo'));
          if (row.images?.length > 1) {
            const count = this.element('span', 'lpr__photo-count', `+${row.images.length - 1}`);
            count.setAttribute('aria-label', this.text('photo_count', { count: row.images.length })); media.append(count);
          }
          card.append(media);
        }
        card.append(this.reviewBody(row)); fragment.append(card);
      });
      this.grid.append(fragment);
      [...this.grid.children].slice(previousCount).forEach(card => this.resizeObserver.observe(card));
      this.querySelector('[data-more]').hidden = this.grid.children.length >= this.filtered.length;
      this.status.textContent = this.filtered.length ? this.text('results', { shown: this.grid.children.length, total: this.filtered.length }) : this.text('empty');
      this.status.classList.toggle('lpr__status--quiet', this.filtered.length > 0); this.layout();
    }
    scheduleLayout() { cancelAnimationFrame(this.layoutFrame); this.layoutFrame = requestAnimationFrame(() => this.layout()); }
    layout() {
      if (!this.isConnected || !this.grid.clientWidth) return;
      const styles = getComputedStyle(this.grid), columns = Number(styles.getPropertyValue('--columns')), gap = Number(styles.getPropertyValue('--gap'));
      const width = (this.grid.clientWidth - gap * (columns - 1)) / columns, heights = Array(columns).fill(0), cards = [...this.grid.children];
      cards.forEach(card => { card.style.width = `${width}px`; });
      for (const card of cards) {
        const column = heights.indexOf(Math.min(...heights)); card.style.transform = `translate(${column * (width + gap)}px, ${heights[column]}px)`;
        heights[column] += card.getBoundingClientRect().height + gap;
      }
      this.grid.style.height = `${Math.max(0, ...heights) - (cards.length ? gap : 0)}px`;
    }
    showReview(index, trigger) {
      const row = this.filtered[index]; if (!row) return;
      this.closePanels(); this.returnFocus = trigger || document.activeElement;
      const body = this.querySelector('[data-viewer-body]'); body.replaceChildren();
      this.viewer.classList.toggle('lpr__dialog--text', !row.image);
      this.viewerRow = row; this.viewerPhoto = 0;
      if (row.image) {
        const media = this.element('div', 'lpr__viewer-media'); media.append(this.image(row, 'lpr__viewer-image', false));
        if (row.images?.length > 1) {
          const controls = this.element('div', 'lpr__photo-controls');
          for (const [step, label, symbol] of [[-1, 'previous_photo', '‹'], [1, 'next_photo', '›']]) {
            const button = this.element('button', 'lpr__photo-step', symbol); button.type = 'button'; button.dataset.photoStep = step;
            button.setAttribute('aria-label', this.text(label)); controls.append(button);
          }
          const counter = this.element('span', 'lpr__photo-counter', this.text('photo_counter', { current: 1, total: row.images.length }));
          counter.setAttribute('aria-live', 'polite'); controls.insertBefore(counter, controls.lastChild); media.append(controls);
        }
        body.append(media);
      }
      const detail = this.element('div', 'lpr__viewer-detail'), heading = this.element('div', 'lpr__viewer-heading'), meta = this.element('div', 'lpr__viewer-meta');
      const title = this.element('div', 'lpr__viewer-title'); title.append(this.element('strong', 'lpr__name', row.name));
      if (row.sourceVerified === true) {
        const verification = this.element('span', 'lpr__verification'); verification.append(this.verifiedBadge(true));
        const note = this.element('span', 'lpr__verification-note', 'ⓘ'); note.tabIndex = 0; note.setAttribute('role', 'img'); note.setAttribute('aria-label', this.text('verified_source')); verification.append(note); title.append(verification);
      }
      heading.append(title); meta.append(this.stars(row.rating), this.date(row.date)); heading.append(meta);
      const content = this.element('div', 'lpr__viewer-content'); content.append(this.reviewText(row));
      if (row.purchaseDate) {
        const purchase = this.element('div', 'lpr__purchase'); purchase.append(this.element('span', '', this.text('purchase_date')), this.date(row.purchaseDate)); content.append(purchase);
      }
      detail.append(heading, content); body.append(detail); this.viewer.showModal(); this.viewer.scrollTop = 0;
    }
    changePhoto(step) {
      const row = this.viewerRow; if (!row?.images?.length) return;
      this.viewerPhoto = (this.viewerPhoto + step + row.images.length) % row.images.length;
      const photo = { ...row, ...row.images[this.viewerPhoto] };
      this.viewer.querySelector('.lpr__viewer-image').replaceWith(this.image(photo, 'lpr__viewer-image', false));
      this.viewer.querySelector('.lpr__photo-counter').textContent = this.text('photo_counter', { current: this.viewerPhoto + 1, total: row.images.length });
    }
    closePanels(restore = false) {
      for (const selector of ['[data-ratings-toggle]', '[data-sort-toggle]']) {
        const button = this.querySelector(selector), panel = this.querySelector(`#${CSS.escape(button.getAttribute('aria-controls'))}`);
        if (restore && !panel.hidden) button.focus(); panel.hidden = true; button.setAttribute('aria-expanded', 'false');
      }
    }
    togglePanel(button) {
      const opening = button.getAttribute('aria-expanded') !== 'true'; this.closePanels();
      if (opening) { this.querySelector(`#${CSS.escape(button.getAttribute('aria-controls'))}`).hidden = false; button.setAttribute('aria-expanded', 'true'); this.load(); }
    }
    handleKey(event) {
      if (event.target.closest('[data-original-toggle]')) return;
      const card = event.target.closest('[data-open-review]');
      if (card && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); this.showReview(Number(card.dataset.openReview), card); }
      if (event.key === 'Escape' && !this.viewer.open) this.closePanels(true);
      if (event.target.matches('[data-sort]') && ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault(); const options = [...this.querySelectorAll('[data-sort]')], index = options.indexOf(event.target);
        options[event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length].focus();
      }
      if (event.target.matches('[data-sort-toggle]') && event.key === 'ArrowDown') {
        event.preventDefault(); if (event.target.getAttribute('aria-expanded') !== 'true') this.togglePanel(event.target); this.querySelector('[data-sort][aria-checked="true"]').focus();
      }
    }
    handleClick(event) {
      const original = event.target.closest('[data-original-toggle]');
      if (original && this.contains(original)) { event.stopPropagation(); this.toggleOriginal(original.dataset.originalToggle); return; }
      const card = event.target.closest('[data-open-review]');
      if (card && this.contains(card)) { if (!window.getSelection()?.toString()) this.showReview(Number(card.dataset.openReview), card); return; }
      const button = event.target.closest('button'); if (!button || !this.contains(button)) return;
      if (button.matches('[data-ratings-toggle], [data-sort-toggle]')) this.togglePanel(button);
      if (button.matches('[data-star]')) { this.rating = this.rating === Number(button.dataset.star) ? null : Number(button.dataset.star); this.applyFilters(); }
      if (button.matches('[data-sort]')) { this.sort = button.dataset.sort; this.closePanels(); this.querySelector('[data-sort-toggle]').focus({ preventScroll: true }); this.applyFilters(); }
      if (button.matches('[data-retry]')) this.load();
      if (button.matches('[data-more]')) { const start = this.grid.children.length; this.limit += 20; this.renderNext(); this.grid.children[start]?.focus({ preventScroll: true }); }
      if (button.matches('[data-photo-step]')) this.changePhoto(Number(button.dataset.photoStep));
      if (button.matches('[data-close]')) button.closest('dialog').close();
    }
  });
}
